// deps
import React, { forwardRef, memo, useCallback, useMemo, useRef } from "react";

// internal deps
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import {
    IRightActionDetails,
    RightActionTypes,
    TextFieldTypes,
} from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { isEmpty } from "@swiggy-private/js-utils";

// styles
import { Container } from "./styles";

// constants
import {
    APPLY_COUPON_INPUT_BOX_DATA_TEST_ID,
    COUPON_CARD_CTA_TEXTS,
    INPUT_BOX_PLACEHOLDER,
} from "@core-ui/constants/Coupon";
import { CouponCardImpressionPayload } from "@swiggy-private/core-types";

// hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

export interface IInputBoxProps {
    isLoading: boolean;
    onApplyClick: (payload: CouponCardImpressionPayload) => void;
    couponCode: string;
    setCouponCode: (value: string) => void;
    handleSearchBarImpression?: (placeholder: string) => void;
    isGiftingCouponListing?: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
}

const ApplyCouponInputBox = forwardRef<HTMLDivElement, IInputBoxProps>(
    (
        {
            isLoading,
            onApplyClick,
            couponCode,
            setCouponCode,
            handleSearchBarImpression,
            isGiftingCouponListing,
            onFocus,
            onBlur,
        },
        ref,
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);

        useEffectOnce(() => {
            if (isGiftingCouponListing) {
                handleSearchBarImpression?.(INPUT_BOX_PLACEHOLDER);
            }
        });

        const handleOnChange = useCallback(
            (val: string) => {
                setCouponCode(val.toUpperCase());
            },
            [setCouponCode],
        );

        const handleOnClick = useCallback(() => {
            onApplyClick({ couponCode });
        }, [couponCode, onApplyClick]);

        const onKeyDown = useCallback(
            (ev: React.KeyboardEvent<HTMLInputElement>) => {
                if (ev.key === "Enter") {
                    inputRef.current?.blur();
                    handleOnClick();
                }
            },
            [handleOnClick],
        );

        const inputBoxDetails = useMemo(() => {
            return {
                value: couponCode,
                type: TextFieldTypes.TEXT,
                name: "coupon-code",
                label: INPUT_BOX_PLACEHOLDER,
                onChange: handleOnChange,
                attrs: {
                    onKeyDown,
                    ...(onFocus && { onFocus }),
                    ...(onBlur && { onBlur }),
                },
            };
        }, [couponCode, handleOnChange, onKeyDown, onFocus, onBlur]);

        const rightActionDetailsDefault = useMemo(() => {
            return [
                {
                    type: RightActionTypes.BUTTON_LABEL,
                    label: COUPON_CARD_CTA_TEXTS.APPLY,
                    onClick: handleOnClick,
                },
            ] as [IRightActionDetails];
        }, [handleOnClick]);

        return (
            <div ref={ref}>
                <Container
                    isEmpty={isEmpty(couponCode) || couponCode?.trim()?.length == 0}
                    isLoading={isLoading}
                    data-testid={APPLY_COUPON_INPUT_BOX_DATA_TEST_ID.CONTAINER}
                >
                    <TextField
                        textFieldDetails={inputBoxDetails}
                        ref={inputRef}
                        rightActionDetails={rightActionDetailsDefault}
                        data-testid={APPLY_COUPON_INPUT_BOX_DATA_TEST_ID.INPUT}
                    />
                </Container>
            </div>
        );
    },
);

if (__DEV__) ApplyCouponInputBox.displayName = "ApplyCouponInputBox";

export default memo(ApplyCouponInputBox);
